import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "About achieving polymorphism in Javascript",
  "description": "",
  "published": "2013-10-15T00:00:00.000Z",
  "redirect_from": ["/blog/about-achieving-polymorphism-in-javascript/swizec/6370"],
  "categories": "Application programming interface, Haskell, JavaScript, Languages, Programming, Toshl, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Over the weekend I started working on a `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/toshl.js"
      }}>{`node.js client library for Toshl's new API`}</a>{`. An old side project stopped working recently and I need to fix it because life without `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/a-message-from-your-future-self/swizec/3377"
      }}>{`frequent emails about my money`}</a>{` is disorienting as hell.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.toshl.com/"
      }}>{`Toshl beta API`}</a>{` opened up in July is well thought out, has good documentation and provides everything you could possibly want. I love the well granulated permissions system.`}</p>
    <p>{`But for users of my node.js library I wanted to make life even simpler. Let's look at fetching expenses.`}</p>
    <p>{`After you authenticate - testing `}<a parentName="p" {...{
        "href": "http://oauth.net",
        "title": "OAuth"
      }}>{`OAuth`}</a>{` clients sucks, and Toshl invalidates your token `}<em parentName="p">{`very`}</em>{` often. Think I had to refresh it four times in a five hour coding spree. - you can talk to `}<inlineCode parentName="p">{`https://api.toshl.com/expenses`}</inlineCode>{` to get a list of expenses for the current user.`}</p>
    <p>{`This will return the last 30 entries.`}</p>
    <p>{`But there are a bunch of options. You have pagination, you can set specific `}<inlineCode parentName="p">{`to`}</inlineCode>{` and `}<inlineCode parentName="p">{`from`}</inlineCode>{` dates, you can filter things by tags and by not-tags.`}</p>
    <p>{`How can a library make this easy to use?`}</p>
    <p>{`One approach is to offer a way of specifying an options hash when calling a function, but can we make it even simpler?`}</p>
    <p>{`The answer lies in polymorphism.`}</p>
    <p>{`Let's say you have a function called `}<inlineCode parentName="p">{`toshl.expenses`}</inlineCode>{`, in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{` you could do something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`expenses::Result
expenses = general_expenses ""

expenses::Number -> Result
expenses N = general_expenses "?per_page="+str(N)

expenses::[String] -> Result
expenses tags = general_expenses "?tags="+(tags.join "&")

expenses::Date -> Date -> Result
expenses from to = general_expenses "?from="+str(from)+"&"+str(to)

expenses::Json -> Result
expenses params = general_expenses "?"+to_query(params)

general_expenses::String -> Result
general_expenses query = make_request "/expenses" query

make_request::String -> String -> Result
make_request endpoint query = ;; do stuff to read from full URL
`}</code></pre>
    <p>{`The syntax is likely wrong but you get what I'm pointing at. You can always call `}<inlineCode parentName="p">{`expenses`}</inlineCode>{` with the argument you care about and it will construct a call to the more generalized version of the function magically.`}</p>
    <p>{`You can tell what calling `}<inlineCode parentName="p">{`expenses`}</inlineCode>{` will do in each case at a glance and checking what type of arguments the function accepts is trivial. Even somebody who isn't familiar with Haskell could understand that code.`}</p>
    <p>{`Here's that same polymorphic code in `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`Javascript`}</a>{`. This time tested working code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`exports.Toshl.prototype.expenses = function (params, to, callback) {
  var options = {},
    query = "";
  callback = arguments[arguments.length - 1];

  if (params) {
    if (typeof params == "number") {
      options["per_page"] = params;
    } else if (arguments.length == 3) {
      options["from"] = util.iso_date(params);
      options["to"] = util.iso_date(to);
    } else if (params instanceof Array) {
      var tags = util.transform_tags(params);
      options[tags.type] = tags.tags;
    } else if (params instanceof Object) {
      options = params;

      ["from", "to"].forEach(function (key) {
        if (options[key]) {
          options[key] = util.iso_date(options[key]);
        }
      });
    }

    query = "?" + querystring.stringify(options);
  }

  this._request("/expenses" + query, callback);
};
`}</code></pre>
    <p>{`Oh wow, what?`}</p>
    <p>{`Even if you're very comfortable with Javascript you're going to have a hard time figuring out what's going on. It seems the majority of the function deals with translating `}<inlineCode parentName="p">{`arguments`}</inlineCode>{` into a `}<inlineCode parentName="p">{`query`}</inlineCode>{`, then it defers to `}<inlineCode parentName="p">{`this._request`}</inlineCode>{` for the hard work.`}</p>
    <p>{`This is the cleanest implementation I could think of so far. Let's investigate.`}</p>
    <p>{`First we ensured that `}<inlineCode parentName="p">{`callback`}</inlineCode>{` is always the last supplied argument, that makes sense when you expect variable amounts of arguments. Everything from one to three is okay.`}</p>
    <p>{`Then, if `}<inlineCode parentName="p">{`params`}</inlineCode>{` is a number we use it to construct a `}<inlineCode parentName="p">{`?per_page=N`}</inlineCode>{` query. If there are three arguments, we use the first two to construct a `}<inlineCode parentName="p">{`?from=Date&to=Date`}</inlineCode>{` query. If the first argument is an Array we use it to get tags and if it's an Object we assume it represents a parameters hash for the API.`}</p>
    <p>{`The result is that we can do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var toshl = new Toshl();

toshl.expenses(console.log); // prints last 30 expenses

toshl.expenses(5, console.log); // prints last 5 expenses

toshl.expenses("2013-10-01", new Date(), console.log); // prints all expenses between October 1st and now

toshl.expenses(["coffee", "food"], console.log); // prints last 30 expenses tagged with coffee or food

toshl.expenses({ per_page: 10, tags: ["coffee", "food"] }, console.log); // prints last 10 expenses tagged coffee or food
`}</code></pre>
    <p>{`The date magic is done with the wonderful `}<a parentName="p" {...{
        "href": "http://momentjs.com/"
      }}>{`moment.js`}</a>{` library - you can supply a Date object, a date string or a moment object, but we could obviously improve on this by assuming the `}<inlineCode parentName="p">{`to`}</inlineCode>{` date is "now", if none is provided.`}</p>
    <p>{`But the code is getting complicated as it is.`}</p>
    <p>{`Does anyone know a better way to achieve polymorphism in Javascript? I really like it when I'm using libraries, but I hate implementing it this way ...`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=739c8f9e-f369-4125-9471-ec1a4738359c",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      